import React, { useMemo } from 'react';
import { PAYMENT_METHOD_TYPES } from '@/lib/constants';
import AfterPayIcon from '@/assets/icons/income/afterpay.svg';
import CardIcon from '@/assets/icons/income/card.svg';
import IdealIcon from '@/assets/icons/income/ideal.svg';
import KlarnaIcon from '@/assets/icons/income/klarna.svg';
import LinkIcon from '@/assets/icons/income/link.svg';
import OtherIcon from '@/assets/icons/income/other.svg';
import PaypalIcon from '@/assets/icons/income/paypal.svg';
import { ProductPayment, TransactionCalculations } from '../types/transactionTypes';
import {
    formatPreConversionAmountDisplay,
    getPaymentDisplayFlags,
    getPaymentGatewayDisplayName,
    getPaymentMethodType,
} from '../income.logic';
import { FeeBreakdownV2Tooltip } from '../components/FeeBreakdownV2Tooltip';
import { LegacyFeeBreakdownTooltip } from '../components/LegacyFeeBreakdownTooltip';

export function useTransactionCalculations(paymentData: ProductPayment): TransactionCalculations {
    const method = paymentData?.data?.payment_method_type;

    const paymentMethodIcon = useMemo(() => {
        switch (method) {
            case PAYMENT_METHOD_TYPES.CARD:
                return CardIcon;
            case PAYMENT_METHOD_TYPES.KLARNA:
                return KlarnaIcon;
            case PAYMENT_METHOD_TYPES.AFTERPAY:
                return AfterPayIcon;
            case PAYMENT_METHOD_TYPES.LINK:
                return LinkIcon;
            case PAYMENT_METHOD_TYPES.IDEAL:
                return IdealIcon;
            case PAYMENT_METHOD_TYPES.PAYPAL:
                return PaypalIcon;
            default:
                return OtherIcon;
        }
    }, [method]);

    const paymentMethodType = useMemo(() => {
        return getPaymentMethodType(method);
    }, [method]);

    const paymentGatewayDisplayName = useMemo(() => {
        return getPaymentGatewayDisplayName(paymentData?.payment_gateway);
    }, [paymentData?.payment_gateway]);

    const posPreConversionAmountDisplay = useMemo(() => {
        return formatPreConversionAmountDisplay(paymentData);
    }, [paymentData]);

    const displayFlags = useMemo(() => {
        return getPaymentDisplayFlags(paymentData);
    }, [paymentData]);

    const tooltipContent = useMemo(() => {
        const feeBreakdownV2 = paymentData?.pos_data?.fee_breakdown_v2;
        if (feeBreakdownV2) {
            return <FeeBreakdownV2Tooltip feeBreakdownV2={feeBreakdownV2} paymentMethodType={paymentMethodType} />;
        } else {
            return <LegacyFeeBreakdownTooltip paymentData={paymentData} paymentMethodType={paymentMethodType} />;
        }
    }, [paymentData, paymentMethodType]);

    return {
        paymentMethodIcon,
        paymentMethodType,
        paymentGatewayDisplayName,
        posPreConversionAmountDisplay,
        showTaxAmount: displayFlags.showTaxAmount,
        showGrossAmount: displayFlags.showGrossAmount,
        tooltipContent,
    };
}
